import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'
import { flushSync } from 'react-dom'
import { Input }  from '@/components/ui/input'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel } from '@/components/ui/select'
import { ChevronDown, Search, X } from 'lucide-react'
import { cn, OVERLAY_Z_INDEX, toCssLength, toStyleObject } from '@/lib/utils'
import { createGeneralApi, clearListeners } from '../tbsGeneralApi'
import { useT } from '@/lib/i18n'

// Libellé du champ tronqué avec ellipsis (au lieu de repasser à la ligne) + tooltip natif posé
// dynamiquement au survol, uniquement si le texte est effectivement tronqué — même convention que
// LabelField.jsx (type 8). `text` porte le libellé brut pour le tooltip (indépendant de `children`,
// qui peut inclure l'astérisque `required`, à ne pas répéter dans le `title`).
const FIELD_LABEL_CLASS = 'text-[11px] font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-wide leading-none truncate block max-w-full'
function FieldLabel({ htmlFor, text, children }) {
  const ref = useRef(null)
  const handleMouseEnter = () => {
    const el = ref.current
    if (!el) return
    if (el.scrollWidth > el.clientWidth) el.title = text
    else el.removeAttribute('title')
  }
  return htmlFor
    ? <label ref={ref} htmlFor={htmlFor} className={FIELD_LABEL_CLASS} onMouseEnter={handleMouseEnter}>{children}</label>
    : <span  ref={ref}                   className={FIELD_LABEL_CLASS} onMouseEnter={handleMouseEnter}>{children}</span>
}

// Texte de valeur tronqué avec ellipsis (une seule ligne) + tooltip natif posé dynamiquement au
// survol, uniquement si effectivement tronqué — même convention que FieldLabel, appliquée cette
// fois au libellé de l'item sélectionné/affiché (trigger, mode lecture, liste déroulante), pas au
// libellé du champ lui-même. `text` porte le texte brut pour le tooltip et sert de contenu par
// défaut ; `children`, s'il est fourni (ex. <SelectValue>), est affiché à la place mais mesuré via
// le même wrapper (le DOM interne de <SelectValue> n'étant pas mesurable/contrôlable ici).
function Truncated({ text, className, children }) {
  const ref = useRef(null)
  const handleMouseEnter = () => {
    const el = ref.current
    if (!el) return
    if (el.scrollWidth > el.clientWidth) el.title = text
    else el.removeAttribute('title')
  }
  return (
    <span ref={ref} className={cn('truncate block min-w-0', className)} onMouseEnter={handleMouseEnter}>
      {children ?? text}
    </span>
  )
}

// Supporte [code, libellé], [code, libellé, sélectionné] et [code, libellé, sélectionné, isGroup]
function toOption([val, lbl, sel, grp]) {
  return { code: String(val), label: String(lbl), selected: !!sel, isGroup: !!grp }
}

// Regroupe les options normalisées en segments selon les items marqués isGroup
function buildGroups(opts) {
  const groups = []
  let current = null
  for (const opt of opts) {
    if (opt.isGroup) {
      current = { groupLabel: opt.label, items: [] }
      groups.push(current)
    } else {
      if (!current) { current = { groupLabel: null, items: [] }; groups.push(current) }
      current.items.push(opt)
    }
  }
  return groups
}

function sortOpts(opts) {
  return [...opts].sort((a, b) => {
    if (a.isGroup || b.isGroup) return 0
    return a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })
  })
}

// Filtre texte local (combo statique mono/multi, proxyLoader non concerné — déjà un filtre serveur) :
// n'entre en vigueur qu'au-delà de 3 caractères saisis (même seuil que proxyLoader/`searchMinChars`,
// en dessous la liste complète reste affichée telle quelle). Sous-chaîne insensible à la casse sur le
// libellé. Les entêtes `isGroup` sont conservées provisoirement (filtrées après coup si leur groupe
// ne contient plus aucun item correspondant), pour ne pas afficher un entête vide.
const FILTER_MIN_CHARS = 3
function filterOptsByQuery(opts, query) {
  const q = query.trim().toLowerCase()
  if (q.length <= FILTER_MIN_CHARS) return opts
  const filtered = opts.filter(o => o.isGroup || o.label.toLowerCase().includes(q))
  return filtered.filter((o, i) => !o.isGroup || (filtered[i + 1] && !filtered[i + 1].isGroup))
}

export default function ComboField({ id, label, value, items, proxyLoader, multiSelect, mode, required, placeholder, autoSort, visible, onEvent, compoGroup, userData, dynaComboLoader, class: cssClass, style: rawStyle }) {
  const t = useT()
  const cssStyle = toStyleObject(rawStyle)
  // `visible: false` (JSON) → display:none posé directement sur le div [data-tbs-root], pas sur
  // un wrapper externe : show()/hide() (API générale, tbsGeneralApi.js) ciblent ce même élément.
  const visibilityStyle = visible === false ? { display: 'none' } : undefined
  // ── State ──────────────────────────────────────────────────────────
  const [prevValue, setPrevValue]     = useState(value)
  // Items mutables (modifiables par addItem/removeItem/etc.)
  const [localItems, setLocalItems]   = useState(items ?? [])
  const [local, setLocal]             = useState(() => {
    if (multiSelect) return ''
    if (value != null) return String(value)
    const list = items ?? []
    const pre = list.find(item => item[2] === true && !item[3])
    if (pre) return String(pre[0])
    // Combo statique (proxyLoader absent) sans aucun item sélectionné dans le JSON : présélectionne
    // le premier item non-groupe de la liste plutôt que de laisser le champ vide.
    if (!proxyLoader && list.length > 0) {
      const first = list.find(item => !item[3])
      if (first) return String(first[0])
    }
    return ''
  })
  const [selected, setSelected]       = useState(() => {
    if (!multiSelect) return new Set()
    if (Array.isArray(value)) return new Set(value.map(String))
    return new Set(
      (items ?? []).filter(item => item[2] === true && !item[3]).map(item => String(item[0]))
    )
  })
  // Combo proxyLoader avec `value` JSON ou item `items` marqué `true` : le champ texte doit
  // afficher le libellé correspondant dès le montage (`local` porte déjà le code, voir ci-dessus)
  // plutôt que rester vide jusqu'à la prochaine recherche ajax.
  const [inputText, setInputText]     = useState(() => {
    if (multiSelect || !proxyLoader) return ''
    const list = items ?? []
    if (value != null) {
      const match = list.find(item => String(item[0]) === String(value) && !item[3])
      return match ? String(match[1]) : ''
    }
    const pre = list.find(item => item[2] === true && !item[3])
    return pre ? String(pre[1]) : ''
  })
  const [ajaxOptions, setAjaxOptions] = useState([])
  const [ajaxOpen, setAjaxOpen]       = useState(false)
  const [msOpen, setMsOpen]           = useState(false)
  const [monoOpen, setMonoOpen]       = useState(false)
  const [error, setError]             = useState(false)
  // Filtre texte local du popup (mono statique via Radix Select) et du dropdown multi-select —
  // distinct de `inputText` (proxyLoader, filtre serveur) : jamais de valeur sélectionnée associée,
  // remis à '' à chaque fermeture du popup/dropdown.
  const [monoFilter, setMonoFilter]   = useState('')
  const [msFilter, setMsFilter]       = useState('')
  const monoFilterInputRef = useRef(null)
  const msFilterInputRef   = useRef(null)
  const [modeState, setModeState]               = useState(mode)
  const [requiredState, setRequiredState]       = useState(!!required)
  const [placeholderState, setPlaceholderState] = useState(placeholder)
  const [disabledState, setDisabledState]       = useState(false)
  const [tooltip, setTooltip]                   = useState(undefined)
  const debounce    = useRef(null)
  const wrapperRef  = useRef(null)
  // Élément visuel du champ lui-même (trigger statique, bouton multiSelect, input proxyLoader, div
  // d'affichage lecture seule) — distinct du div [data-tbs-root] (wrapper englobant label + champ +
  // message d'erreur) ciblé par les méthodes générales de tbsGeneralApi.js. setWidth()/getWidth()/
  // setHeight()/getHeight() sont surchargées ci-dessous pour cibler cette ref plutôt que le wrapper
  // (même raison que ChampField.jsx : `flex-col` n'étire pas ses enfants en hauteur).
  const fieldElRef  = useRef(null)
  // Nombre de caractères minimum avant déclenchement de tbsDynaComboLoader() (proxyLoader) —
  // configurable via setMinChars(), lu directement dans handleProxySearch (pas besoin de re-render).
  const minCharsRef = useRef(3)

  // ── Refs pour tbsComponents ────────────────────────────────────────
  const localRef         = useRef(local)
  const selectedRef      = useRef(selected)
  const inputTextRef     = useRef(inputText)
  const localItemsRef    = useRef(localItems)
  const compoGroupRef    = useRef(compoGroup ?? null)
  const userDataRef      = useRef(userData ?? null)
  const onEventRef       = useRef(onEvent)
  const disabledRef      = useRef(false)
  const requiredStateRef = useRef(!!required)
  const modeStateRef     = useRef(mode)
  useLayoutEffect(() => { localRef.current = local })
  useLayoutEffect(() => { selectedRef.current = selected })
  useLayoutEffect(() => { inputTextRef.current = inputText })
  useLayoutEffect(() => { localItemsRef.current = localItems })
  useLayoutEffect(() => { onEventRef.current = onEvent })
  useLayoutEffect(() => { disabledRef.current = disabledState })
  useLayoutEffect(() => { requiredStateRef.current = requiredState })
  useLayoutEffect(() => { modeStateRef.current = modeState })

  // Options statiques normalisées (items de groupe exclus des valeurs sélectionnables)
  const staticOpts = useMemo(() => {
    const opts = localItems.map(toOption)
    return autoSort ? sortOpts(opts) : opts
  }, [localItems, autoSort])

  // ── Enregistrement tbsComponents ───────────────────────────────────
  useEffect(() => {
    window.tbsComponents = window.tbsComponents ?? {}

    const applyAutoSort = (arr) => autoSort ? sortOpts(arr) : arr

    const common = {
      ...createGeneralApi(id, userDataRef.current),
      // Cf. commentaire sur fieldElRef plus haut — cible le champ lui-même, pas [data-tbs-root].
      setWidth:  width  => { const el = fieldElRef.current; if (el) el.style.width  = toCssLength(width) },
      getWidth:  ()      => fieldElRef.current ? fieldElRef.current.offsetWidth : undefined,
      setHeight: height => { const el = fieldElRef.current; if (el) el.style.height = toCssLength(height) },
      getHeight: ()      => fieldElRef.current ? fieldElRef.current.offsetHeight : undefined,
      getCompoGroup: () => compoGroupRef.current,
      setCompoGroup: v => { compoGroupRef.current = v },
      setReadOnly:    v => { const b = !!v; modeStateRef.current = b ? 'read' : 'write'; setModeState(modeStateRef.current) },
      isReadOnly:     () => modeStateRef.current === 'read',
      setMandatory:   () => { requiredStateRef.current = true;  setRequiredState(true) },
      unsetMandatory: () => { requiredStateRef.current = false; setRequiredState(false) },
      isRequired:     () => requiredStateRef.current,
      setRequired:    v => { const b = !!v; requiredStateRef.current = b; setRequiredState(b) },
      setMask:        () => { /* sans effet sur les composants de type 4 */ },
      setPlaceholder: placeholderText => setPlaceholderState(placeholderText),
      setTooltip:     tooltipText => setTooltip(tooltipText),
      setDisabled: v => { const b = !!v; setDisabledState(b); disabledRef.current = b },
      isDisabled:  () => disabledRef.current,
      setFocus:       () => document.getElementById(id)?.focus(),
      setMinChars:    n => { minCharsRef.current = Number(n) },

      // Gestion des items — localItemsRef/selectedRef/localRef assignés de façon synchrone avant
      // chaque setState (jamais à l'intérieur d'un updater setState(prev => ...), non garanti
      // synchrone) : sans cela, un getCurText()/getValue() appelé juste après lirait encore
      // l'ancien état (refs resynchronisées seulement au rendu suivant).
      addItem: (code, libelle, sel, isGroup) => {
        const codeStr = String(code)
        const opts = localItemsRef.current.map(toOption)
        let next = localItemsRef.current
        if (!opts.some(o => o.code === codeStr)) {
          const newItem = [codeStr, String(libelle ?? ''), !!sel, !!isGroup]
          const merged = [...localItemsRef.current, newItem]
          next = autoSort ? applyAutoSort(merged.map(toOption)).map(o => [o.code, o.label, o.selected, o.isGroup]) : merged
        }
        localItemsRef.current = next
        setLocalItems(next)
        // flushSync sur la valeur sélectionnée (pas sur localItems, hors sujet FormData) : même
        // raison que ChampField/ComboField.setValue — un submit() enchaîné juste après lirait
        // sinon encore l'ancienne valeur du DOM.
        if (!multiSelect && sel) { localRef.current = codeStr; flushSync(() => setLocal(codeStr)) }
        if (multiSelect && sel) {
          const s = new Set([...selectedRef.current, codeStr])
          selectedRef.current = s
          flushSync(() => setSelected(s))
        }
      },
      addItems: (itemsArray) => {
        const opts = localItemsRef.current.map(toOption)
        const existingCodes = new Set(opts.map(o => o.code))
        const newOnes = (itemsArray ?? []).filter(item => !existingCodes.has(String(item[0])))
        let next = localItemsRef.current
        if (newOnes.length) {
          const merged = [...localItemsRef.current, ...newOnes]
          next = autoSort ? applyAutoSort(merged.map(toOption)).map(o => [o.code, o.label, o.selected, o.isGroup]) : merged
        }
        localItemsRef.current = next
        setLocalItems(next)
      },
      getCurText: () => {
        // Recalculé à partir de localItemsRef.current (jamais du `staticOpts` mémoïsé du rendu :
        // cette fonction vit dans une closure d'effet à deps [id, multiSelect, modeState,
        // requiredState] qui ne se recrée pas quand localItems change via addItem()/removeItem()).
        const opts = applyAutoSort(localItemsRef.current.map(toOption))
        if (multiSelect) {
          const labels = opts.filter(o => !o.isGroup && selectedRef.current.has(o.code)).map(o => o.label)
          return labels.length > 0 ? labels : null
        }
        if (proxyLoader) return inputTextRef.current || null
        const opt = opts.filter(o => !o.isGroup).find(o => o.code === localRef.current)
        return opt ? opt.label : null
      },
      getNbItems: () => applyAutoSort(localItemsRef.current.map(toOption)).length,
      getItemAt:  itemPos => applyAutoSort(localItemsRef.current.map(toOption))[itemPos]?.code ?? null,
      // Même format que l'attribut JSON `items` : [code, libellé, sélectionné, isGroup]. `sélectionné`
      // reflète la sélection courante (pas le flag JSON d'origine, qui devient obsolète après un
      // setValue()/une interaction utilisateur) — toujours `false` pour un item de groupe.
      getItems: () => applyAutoSort(localItemsRef.current.map(toOption)).map(o => [
        o.code,
        o.label,
        o.isGroup ? false : (multiSelect ? selectedRef.current.has(o.code) : o.code === localRef.current),
        o.isGroup,
      ]),
      removeItem: (code) => {
        const codeStr = String(code)
        const next = localItemsRef.current.filter(item => String(item[0]) !== codeStr)
        localItemsRef.current = next
        setLocalItems(next)
        if (!multiSelect && localRef.current === codeStr) { localRef.current = ''; flushSync(() => setLocal('')); setError(false) }
        if (multiSelect && selectedRef.current.has(codeStr)) {
          const s = new Set(selectedRef.current); s.delete(codeStr)
          selectedRef.current = s
          flushSync(() => setSelected(s))
        }
      },
      removeAllItems: () => {
        localItemsRef.current = []
        setLocalItems([])
        localRef.current = ''
        const s = new Set()
        selectedRef.current = s
        flushSync(() => { setLocal(''); setSelected(s) })
        setError(false)
      },
    }

    const isEmpty = v => v === '' || v === '__@@_vide__()'

    window.tbsComponents[id] = multiSelect
      ? {
          ...common,
          type: 'combo',
          multiSelect: true,
          getValue:      () => [...selectedRef.current],
          // flushSync : le combo porte l'input caché natif soumis par tbsGlobalForm.submit()
          // (HTMLFormElement.submit(), sans event submit) — sans flush synchrone, un submit()
          // appelé juste après ce setValue() (même traitement métier) lirait encore l'ancienne
          // valeur du DOM, le commit React n'ayant pas encore eu lieu (même piège que
          // ChampField.setValue).
          setValue:      (codes, fireEvent) => { const next = new Set(Array.isArray(codes) ? codes.map(String) : []); flushSync(() => setSelected(next)); selectedRef.current = next; setError(false); if (fireEvent) onEventRef.current(id, 'onChange', [...next]) },
          validate: () => {
            if (!requiredStateRef.current || modeStateRef.current === 'read') return true
            const isEmptyVal = localItemsRef.current.length === 0 || selectedRef.current.size === 0
            setError(isEmptyVal)
            return !isEmptyVal
          },
        }
      : {
          ...common,
          type: 'combo',
          multiSelect: false,
          getValue:      () => localRef.current,
          // flushSync : même raison que ci-dessus (mono) — voir setValue multi.
          setValue:      (v, fireEvent) => { const val = v ?? ''; flushSync(() => setLocal(val)); localRef.current = val; setInputText(''); setError(false); if (fireEvent) onEventRef.current(id, 'onChange', val) },
          validate: () => {
            if (!requiredStateRef.current || modeStateRef.current === 'read') return true
            const isEmptyVal = (!proxyLoader && localItemsRef.current.length === 0) || isEmpty(localRef.current) || localRef.current == null
            setError(isEmptyVal)
            return !isEmptyVal
          },
        }
    return () => { delete window.tbsComponents[id]; clearListeners(id) }
  }, [id, multiSelect, modeState, requiredState]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync prop value → state ────────────────────────────────────────
  if (prevValue !== value) {
    setPrevValue(value)
    if (multiSelect) {
      if (Array.isArray(value)) setSelected(new Set(value.map(String)))
    } else {
      setLocal(value ?? '')
      if (proxyLoader) setInputText('')
    }
  }

  // ── Fermeture dropdown proxyLoader au clic extérieur ──────────────
  useEffect(() => {
    if (!proxyLoader) return
    const handler = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setAjaxOpen(false)
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [proxyLoader])

  // ── Fermeture multi-select au clic extérieur ───────────────────────
  useEffect(() => {
    if (!multiSelect) return
    const handler = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) { setMsOpen(false); setMsFilter('') }
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [multiSelect])

  // ── Focus du champ de recherche à l'ouverture du dropdown multi-select. Remise à zéro du filtre
  // à la fermeture gérée au niveau des déclencheurs (clic bouton/extérieur), pas ici (setState
  // synchrone dans un effet → cascading renders évitables).
  useEffect(() => {
    if (multiSelect && msOpen) msFilterInputRef.current?.focus()
  }, [multiSelect, msOpen])

  // ── Focus du champ de recherche à l'ouverture du popup mono statique (Radix Select) — `Content` ne
  // supporte pas `onOpenAutoFocus` (contrairement à Popover/DropdownMenu/Dialog — Select gère son
  // focus par roving tabindex sur les items). Radix pose lui-même le focus sur l'item sélectionné
  // (`focusSelectedItem`) dans un effet interne déclenché par le positionnement popper (`isPositioned`,
  // asynchrone — attente d'un `ResizeObserver`/`requestAnimationFrame`), qui s'exécute *après* le
  // premier passage de tous nos propres effets et regagne donc la course au focus. `setTimeout` (au
  // lieu d'un focus direct) diffère notre prise de focus après ce cycle interne.
  useEffect(() => {
    if (proxyLoader || multiSelect || !monoOpen) return
    const t = setTimeout(() => monoFilterInputRef.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [proxyLoader, multiSelect, monoOpen])

  // ── Callbacks ──────────────────────────────────────────────────────
  const handleKeyDown = useCallback(e => {
    // `preventDefault` bloque l'action par défaut du navigateur pour Entrée dans un champ texte —
    // la soumission implicite du <form> ancêtre (tbsGenericComponentFrm côté JSP) — qui se
    // déclencherait sinon en plus du traitement métier de 'onEnterKeyPress' (lequel peut lui-même,
    // volontairement, déclencher un submit explicite ailleurs, ex. la recherche universelle),
    // causant un double submit serveur.
    if (e.key === 'Enter') { e.preventDefault(); onEvent(id, 'onEnterKeyPress') }
  }, [id, onEvent])

  const handleProxySearch = useCallback(e => {
    const token = e.target.value
    setInputText(token)
    setLocal('')
    if (token) setError(false)
    if (!token || token.length < minCharsRef.current) { setAjaxOptions([]); setAjaxOpen(false); return }
    clearTimeout(debounce.current)
    debounce.current = setTimeout(async () => {
      try {
        const items = await (dynaComboLoader || window.tbsDynaComboLoader)(
          proxyLoader.typeLoading,
          proxyLoader.dynafilter,
          id,
          token
        )
        const opts = (Array.isArray(items) ? items : []).map(toOption)
        setAjaxOptions(autoSort ? sortOpts(opts) : opts)
        setAjaxOpen(true)
      } catch (e) {
        console.error(e)
        setAjaxOptions([])
        setAjaxOpen(false)
      }
    }, 300)
  }, [id, proxyLoader, autoSort, dynaComboLoader])

  // flushSync sur la valeur (avant onEvent) — même raison que ChampField/ComboField.setValue :
  // sans flush synchrone, l'input caché ({id}-val, soumis par tbsGlobalForm.submit()) porterait
  // encore l'ancienne valeur si du code métier le lisait de façon synchrone pendant le traitement
  // de 'onChange' (avant que React n'ait commité le nouveau rendu).
  const selectAjaxOption = useCallback(opt => {
    const changed = localRef.current !== opt.code
    localRef.current = opt.code
    flushSync(() => setLocal(opt.code))
    // `inputTextRef` assigné de façon synchrone avant `setInputText` — sans cela, un getCurText()
    // appelé pendant le traitement synchrone de l'event 'onChange' ci-dessous lirait encore
    // l'ancien token tapé par l'utilisateur (setInputText seul n'est resynchronisé dans la ref
    // qu'au rendu suivant, via useLayoutEffect), pas le libellé de l'item sélectionné.
    inputTextRef.current = opt.label
    setInputText(opt.label)
    setAjaxOptions([])
    setAjaxOpen(false)
    setError(false)
    // Re-sélection du même code (valeur inchangée) : pas d'onChange redondant.
    if (changed) onEvent(id, 'onChange', opt.code)
  }, [id, onEvent])

  // Next calculé à partir de selectedRef.current (jamais à l'intérieur de l'updater setState,
  // non garanti synchrone) puis flushSync avant onEvent — voir remarque ci-dessus.
  const toggleOption = useCallback(code => {
    const next = new Set(selectedRef.current)
    next.has(code) ? next.delete(code) : next.add(code)
    selectedRef.current = next
    flushSync(() => setSelected(next))
    if (next.size > 0) setError(false)
    onEvent(id, 'onChange', [...next])
  }, [id, onEvent])

  const clearMulti = useCallback(() => {
    const empty = new Set()
    selectedRef.current = empty
    flushSync(() => setSelected(empty))
    setError(false)
    onEvent(id, 'onChange', [])
  }, [id, onEvent])

  const clearProxy = useCallback(() => {
    localRef.current = ''
    flushSync(() => setLocal(''))
    // Même raison que selectAjaxOption : ref assignée avant setInputText, pour que getCurText()
    // reflète correctement l'effacement pendant le traitement synchrone de l'event 'onChange'.
    inputTextRef.current = ''
    setInputText('')
    setAjaxOptions([])
    setAjaxOpen(false)
    setError(false)
    onEvent(id, 'onChange', '')
  }, [id, onEvent])

  // ── Libellé du bouton multi-select ─────────────────────────────────
  const msDisplayLabel = useMemo(() => {
    if (!multiSelect || selected.size === 0) return placeholderState ?? '—'
    const labels = staticOpts.filter(o => !o.isGroup && selected.has(o.code)).map(o => o.label)
    if (labels.length <= 2) return labels.join(', ')
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`
  }, [multiSelect, selected, staticOpts, placeholderState])

  const asterisk = requiredState && modeState !== 'read'
    ? <span className="ml-0.5 text-[hsl(var(--destructive))]">*</span>
    : null

  const errClass = 'border-[hsl(var(--destructive))] ring-1 ring-[hsl(var(--destructive))]'

  // ── Mode lecture ───────────────────────────────────────────────────
  if (modeState === 'read') {
    if (multiSelect) {
      const labels = staticOpts.filter(o => !o.isGroup && selected.has(o.code)).map(o => o.label)
      return (
        <div className={cn('mb-2 flex flex-col gap-1 max-w-sm', cssClass)} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
          {label && <FieldLabel text={label}>{label}</FieldLabel>}
          <input id={id} type="hidden" name={id} value={JSON.stringify([...selected])} />
          <div ref={fieldElRef} className="px-3 py-2 text-sm rounded-md bg-[hsl(var(--readonly))] text-[hsl(var(--foreground))]">
            <Truncated text={labels.length > 0 ? labels.join(', ') : '—'} />
          </div>
        </div>
      )
    }
    return (
      <div className={cn('mb-2 flex flex-col gap-1 max-w-sm', cssClass)} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <FieldLabel text={label}>{label}</FieldLabel>}
        <input id={id} type="hidden" name={id} value={local ?? ''} />
        <div ref={fieldElRef} className="px-3 py-2 text-sm rounded-md bg-[hsl(var(--readonly))] text-[hsl(var(--foreground))]">
          <Truncated text={(!proxyLoader ? staticOpts.filter(o => !o.isGroup).find(o => o.code === String(local ?? ''))?.label : inputText || local) || '—'} />
        </div>
      </div>
    )
  }

  // ── Multi-select écriture ──────────────────────────────────────────
  if (multiSelect) {
    return (
      <div className={cn('mb-2 flex flex-col gap-1 max-w-sm', cssClass)} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <FieldLabel htmlFor={id} text={label}>{label}{asterisk}</FieldLabel>}
        <input id={`${id}-val`} type="hidden" name={id} value={JSON.stringify([...selected])} />
        <div ref={wrapperRef} className="relative">
          <button
            id={id}
            ref={fieldElRef}
            type="button"
            disabled={disabledState}
            onClick={() => { if (msOpen) setMsFilter(''); setMsOpen(v => !v) }}
            onFocus={() => onEvent(id, 'onFocus', [...selected])}
            onBlur={e => { if (!wrapperRef.current?.contains(e.relatedTarget)) onEvent(id, 'onBlur', [...selected]) }}
            onKeyDown={handleKeyDown}
            className={cn(
              'flex h-9 w-full items-center justify-between rounded-md border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-3 py-2 text-sm shadow-sm hover:bg-[hsl(var(--accent))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]',
              error && errClass
            )}
          >
            <Truncated text={msDisplayLabel} className="text-left" />
            <span className="flex items-center gap-1 ml-2 shrink-0">
              <ChevronDown
                size={14}
                className={cn('text-[hsl(var(--muted-foreground))] transition-transform duration-200', msOpen && 'rotate-180')}
              />
              {selected.size > 0 && (
                <span
                  className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                  onClick={e => { e.stopPropagation(); clearMulti() }}
                >
                  <X size={12} />
                </span>
              )}
            </span>
          </button>
          {msOpen && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] shadow-md">
              <div className="p-1 border-b border-[hsl(var(--border))]">
                <div className="relative">
                  <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] pointer-events-none" />
                  <input
                    ref={msFilterInputRef}
                    type="text"
                    value={msFilter}
                    onChange={e => setMsFilter(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    placeholder={t('searchMinChars')}
                    className="w-full h-7 pl-6 pr-2 text-xs rounded border border-[hsl(var(--input))] bg-[hsl(var(--background))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]"
                  />
                </div>
              </div>
              <ul className="py-1 max-h-52 overflow-y-auto">
                {(() => {
                  const filtered = filterOptsByQuery(staticOpts, msFilter)
                  return filtered.length === 0
                    ? <li className="px-3 py-1.5 text-sm italic text-[hsl(var(--muted-foreground))]">{t('noResult')}</li>
                    : filtered.map((opt, i) =>
                        opt.isGroup
                          ? (
                            <li key={`g-${i}`} className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] select-none pointer-events-none">
                              {opt.label}
                            </li>
                          ) : (
                            <li
                              key={opt.code}
                              className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer select-none hover:bg-[hsl(var(--accent))]"
                              onMouseDown={e => { e.preventDefault(); toggleOption(opt.code) }}
                            >
                              <input
                                type="checkbox"
                                readOnly
                                checked={selected.has(opt.code)}
                                className="pointer-events-none shrink-0"
                              />
                              <Truncated text={opt.label} />
                            </li>
                          )
                      )
                })()}
              </ul>
            </div>
          )}
        </div>
        {error && <p className="text-xs text-[hsl(var(--destructive))]">{t('requiredField')}</p>}
      </div>
    )
  }

  // ── Sélecteur statique simple ──────────────────────────────────────
  if (!proxyLoader) {
    const groups = buildGroups(filterOptsByQuery(staticOpts, monoFilter))
    return (
      <div className={cn('mb-2 flex flex-col gap-1 max-w-sm', cssClass)} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <FieldLabel htmlFor={id} text={label}>{label}{asterisk}</FieldLabel>}
        <input id={`${id}-val`} type="hidden" name={id} value={local} />
        <Select
          value={local}
          disabled={disabledState}
          onOpenChange={open => { setMonoOpen(open); if (!open) setMonoFilter('') }}
          onValueChange={val => { localRef.current = val; flushSync(() => setLocal(val)); setError(false); onEvent(id, 'onChange', val) }}
        >
          <SelectTrigger
            id={id}
            ref={fieldElRef}
            className={error ? errClass : ''}
            onFocus={() => onEvent(id, 'onFocus', local)}
            onBlur={() => onEvent(id, 'onBlur', local)}
            onKeyDown={handleKeyDown}
            suffix={local && (
              <span
                className="flex shrink-0 items-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                onPointerDown={e => { e.preventDefault(); e.stopPropagation() }}
                onClick={e => { e.stopPropagation(); localRef.current = ''; flushSync(() => setLocal('')); setError(false); onEvent(id, 'onChange', '') }}
              >
                <X size={12} />
              </span>
            )}
          >
            <Truncated text={(local && staticOpts.filter(o => !o.isGroup).find(o => o.code === local)?.label) || placeholderState || '—'} className="text-left">
              <SelectValue placeholder={placeholderState ?? '—'} />
            </Truncated>
          </SelectTrigger>
          {/* z-index explicite : le composant peut être rendu à l'intérieur d'une popup
              tbsUserMessages (modalDialogBox...), dont le zIndex est alloué dynamiquement et peut
              dépasser n'importe quel palier fixe modeste — sans ce dépassement le dropdown Radix
              (Portal vers document.body, z-50 par défaut) s'affiche derrière la popup. Même
              constante que PromptCombo (UserMessages.jsx), qui a le même besoin. */}
          <SelectContent style={{ zIndex: OVERLAY_Z_INDEX }}>
            <div className="sticky top-0 z-10 bg-[hsl(var(--popover))] p-1 border-b border-[hsl(var(--border))]">
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] pointer-events-none" />
                <input
                  ref={monoFilterInputRef}
                  type="text"
                  value={monoFilter}
                  onChange={e => setMonoFilter(e.target.value)}
                  // stopPropagation — empêche la frappe de remonter jusqu'au typeahead de Radix Select,
                  // qui interpréterait sinon chaque caractère comme un raccourci de saut vers un item
                  // plutôt que comme une saisie dans ce champ.
                  onKeyDown={e => e.stopPropagation()}
                  placeholder={t('searchMinChars')}
                  className="w-full h-7 pl-6 pr-2 text-xs rounded border border-[hsl(var(--input))] bg-[hsl(var(--background))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]"
                />
              </div>
            </div>
            {groups.length === 0
              ? <div className="px-3 py-1.5 text-sm italic text-[hsl(var(--muted-foreground))]">{t('noResult')}</div>
              : groups.map((g, gi) =>
                  g.groupLabel != null
                    ? (
                      <SelectGroup key={gi}>
                        <SelectLabel>{g.groupLabel}</SelectLabel>
                        {g.items.map(opt => (
                          <SelectItem key={opt.code} value={opt.code}>{opt.label}</SelectItem>
                        ))}
                      </SelectGroup>
                    ) : (
                      g.items.map(opt => (
                        <SelectItem key={opt.code} value={opt.code}>{opt.label}</SelectItem>
                      ))
                    )
                )}
          </SelectContent>
        </Select>
        {error && <p className="text-xs text-[hsl(var(--destructive))]">{t('requiredField')}</p>}
      </div>
    )
  }

  // ── ProxyLoader (recherche avec appel JS) ──────────────────────────
  return (
    <div className={cn('mb-2 flex flex-col gap-1', cssClass)} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
      {label && <FieldLabel htmlFor={id} text={label}>{label}{asterisk}</FieldLabel>}
      <div ref={wrapperRef} className="relative w-full">
        <input id={`${id}-val`} type="hidden" name={id} value={local ?? ''} />
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] pointer-events-none"
          />
          <Input
            id={id}
            ref={fieldElRef}
            type="text"
            value={inputText}
            placeholder={placeholderState ?? t('searchMinChars')}
            disabled={disabledState}
            onChange={handleProxySearch}
            onFocus={() => onEvent(id, 'onFocus', local)}
            onBlur={() => { if (requiredState && !local) setError(true); onEvent(id, 'onBlur', local) }}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            className={cn('pl-8', inputText && 'pr-7', error ? errClass : '')}
          />
          {inputText && (
            <button type="button" tabIndex={-1} onClick={clearProxy}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
              <X size={12} />
            </button>
          )}
        </div>
        {ajaxOpen && (
          <ul className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] shadow-md py-1 max-h-52 overflow-y-auto">
            {ajaxOptions.length > 0
              ? ajaxOptions.map((opt, i) =>
                  opt.isGroup
                    ? (
                      <li key={i} className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] select-none pointer-events-none">
                        {opt.label}
                      </li>
                    ) : (
                      <li key={i}
                        className="px-3 py-1.5 text-sm cursor-pointer hover:bg-[hsl(var(--accent))]"
                        onMouseDown={() => selectAjaxOption(opt)}
                      >
                        {opt.label}
                      </li>
                    )
                )
              : <li className="px-3 py-1.5 text-sm italic text-[hsl(var(--muted-foreground))]">{t('noResult')}</li>
            }
          </ul>
        )}
      </div>
      {error && <p className="text-xs text-[hsl(var(--destructive))]">{t('requiredField')}</p>}
    </div>
  )
}
